import { useEffect, useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks'
import { fetchRunDetails, clearRunDetails, rerunAssessments } from '../slices/runDetailsSlice'
import { deleteRun } from '../slices/runsSlice'
import SidebarNavigation from '../components/SidebarNavigation'
import RunHeader from '../components/RunHeader'
import RunMetadata from '../components/RunMetadata'
import RunSummaryStatistics from '../components/RunSummaryStatistics'
import DetailedResultsTable from '../components/DetailedResultsTable'
import ContainerExecutionsSection from '../components/ContainerExecutionsSection'
import './RunDetailsPage.css'

function RunDetailsPage() {
  const { runId } = useParams<{ runId: string }>()
  const dispatch = useAppDispatch()
  const { current: run, loading, error } = useAppSelector((state) => state.runDetails)

  useEffect(() => {
    if (runId) {
      dispatch(fetchRunDetails(runId))
    }
    return () => {
      dispatch(clearRunDetails())
    }
  }, [dispatch, runId])

  // Poll for updates when run is in progress or assessments are still validating
  const hasValidatingResults = run?.results.some((r) => r.status === 'validating') ?? false
  useEffect(() => {
    if (!run) return
    const isRunActive = run.status === 'running' || run.status === 'pending'
    if (!isRunActive && !hasValidatingResults) return
    const interval = setInterval(() => {
      if (runId) {
        dispatch(fetchRunDetails(runId))
      }
    }, 3000)
    return () => clearInterval(interval)
  }, [dispatch, runId, run?.status, hasValidatingResults])

  const [rerunningAssessments, setRerunningAssessments] = useState(false)

  const handleDelete = useCallback(() => {
    if (runId) {
      dispatch(deleteRun(runId))
    }
  }, [dispatch, runId])

  const handleRerunAssessments = useCallback(async () => {
    if (!run) return
    const completedTaskIds = run.results
      .filter((r) => r.status === 'pass' || r.status === 'fail' || r.status === 'dsq' || r.status === 'disagree')
      .map((r) => r.task_id)
    if (completedTaskIds.length === 0) return
    setRerunningAssessments(true)
    try {
      await dispatch(rerunAssessments(completedTaskIds))
      await dispatch(fetchRunDetails(run.id))
    } finally {
      setRerunningAssessments(false)
    }
  }, [dispatch, run])

  const hasCompletedResults = run?.results.some(
    (r) => r.status === 'pass' || r.status === 'fail' || r.status === 'dsq' || r.status === 'disagree'
  ) ?? false

  return (
    <div data-testid="run-details-page" className="run-details-page">
      <SidebarNavigation />

      <main className="run-details-main app-page-main">
        {loading && (
          <div data-testid="run-details-loading" className="run-details-loading">
            Loading run details...
          </div>
        )}

        {error && (
          <div data-testid="run-details-error" className="run-details-error">
            {error}
          </div>
        )}

        {!loading && !error && !run && (
          <div data-testid="run-details-not-found" className="run-details-not-found">
            Run not found.
          </div>
        )}

        {!loading && run && (
          <>
            <header className="app-page-sticky-header">
              <div className="app-page-sticky-header-inner">
                <RunHeader
                  runName={run.name}
                  status={run.status}
                  errorMessage={run.error_message}
                  onDelete={handleDelete}
                />
              </div>
            </header>

            <RunMetadata
              agentName={run.agent_name}
              agents={run.agents}
              attemptsCount={run.attempts_count}
              refined={run.refined}
              failureCount={run.failure_count}
              failureIds={run.selected_failures}
              tags={run.tags}
              branch={run.branch}
              commitHash={run.commit_hash}
              startedAt={run.started_at}
              finishedAt={run.finished_at}
            />

            <RunSummaryStatistics
              successRate={run.success_rate}
              avgCost={run.avg_cost}
              avgTime={run.avg_time}
              totalQueued={run.failure_count * (run.agents?.length || 1) * (run.attempts_count || 1)}
              completedCount={run.results.filter((r) => r.status === 'pass' || r.status === 'fail' || r.status === 'dsq' || r.status === 'disagree').length}
              agents={run.agents}
              results={run.results}
            />

            <DetailedResultsTable
              results={run.results}
              runId={run.id}
              runStatus={run.status}
              agents={run.agents}
              attemptsCount={run.attempts_count}
            />

            {run.containers && run.containers.length > 0 && (
              <ContainerExecutionsSection containers={run.containers} />
            )}

            {hasCompletedResults && (
              <div className="run-actions" data-testid="run-actions">
                <button
                  className="run-action-btn"
                  data-testid="rerun-assessments-button"
                  onClick={handleRerunAssessments}
                  disabled={rerunningAssessments}
                >
                  {rerunningAssessments ? 'Re-running Assessments...' : 'Re-run Assessments'}
                </button>
              </div>
            )}

          </>
        )}
      </main>
    </div>
  )
}

export default RunDetailsPage
